/**
 * Shared TypeScript types and constants for the Job CRM Contacts UI.
 *
 * Mirrors the validators in `convex/contacts/queries.ts`. We keep
 * hand-rolled types here (rather than inferring from the Convex API) to
 * keep the UI side independent of the validator file and to share
 * display-metadata constants with both the job CRM and the firm
 * directory views.
 */

import {
  Briefcase,
  Building,
  Building2,
  Handshake,
  Landmark,
  ShieldCheck,
  Tag,
  UserSquare,
  type LucideIcon,
} from "lucide-react";
import type { Doc, Id } from "@cvx/_generated/dataModel";
import type { TaskColor } from "@cvx/lib/taskColor.schema";
import type { WorkflowRoleIcon } from "@cvx/tasks/workflowRoles/schema";
import { roleIconComponent } from "@/components/settings/tasks/role-icons";
import type { BadgeColor } from "@/ui/primitives/badge";

// Contact-role definitions live in `src/lib/contact-roles.ts` so they can
// be shared with the precedent library / generate-precedents wizard
// without depending on this UI module. Re-exported below for backwards
// compatibility with existing `from "./types"` imports.
import {
  SOURCE_KIND_META as SHARED_SOURCE_KIND_META,
  SOURCE_KIND_ORDER as SHARED_SOURCE_KIND_ORDER,
  ROLE_META as SHARED_ROLE_META,
  ROLE_ORDER as SHARED_ROLE_ORDER,
  type ContactSourceKind as SharedContactSourceKind,
  type SourceKindMeta as SharedSourceKindMeta,
  type ContactRole as SharedContactRole,
  type RoleMeta as SharedRoleMeta,
} from "@/lib/contact-roles";

// ---------------------------------------------------------------------------
// Public types
// ---------------------------------------------------------------------------

export type ContactSourceKind = SharedContactSourceKind;
export type ContactRole = SharedContactRole;

export type ContactType =
  "person" | "organisation" | "trust" | "authority" | "unknown";

export type FirmContactCategory =
  "bank" | "regulator" | "professional" | "org_member" | "referrer" | "other";

export type ContactStatus = "active" | "merged" | "archived" | "deleted";

export type MergeConfidence = "high" | "medium" | "low";

export type ExposureDirection = "creditor" | "debtor";

export interface ContactSourceLink {
  kind: ContactSourceKind;
  /** _id of the originating row, kept as a string for cross-table neutrality. */
  sourceId: string;
  /** Canonical role enum for filtering and document generation. */
  role?: ContactRole;
  roleLabel: string;
  detail?: string;
  amount?: number;
  exposureDirection?: ExposureDirection;
}

export interface ContactAddress {
  line1?: string;
  line2?: string;
  suburb?: string;
  state?: string;
  postcode?: string;
  country?: string;
  sourceLabel: string;
}

/**
 * Slim summary of a linked firm contact embedded into each
 * `JobContactRow`. Used by the detail panel to render the "from firm"
 * overlay pills without an extra round-trip.
 */
export interface FirmContactOverlay {
  _id: Id<"firmContacts">;
  displayName: string;
  category: FirmContactCategory;
  email?: string;
  phone?: string;
  website?: string;
  abn?: string;
  acn?: string;
  notes?: string;
  linkedUserId?: Id<"users">;
}

/**
 * Job-level CRM contact as returned by `api.contacts.queries.listJobContacts`.
 *
 * Identity is rebuilt by the reconcile pipeline; overlay-style fields
 * (email, phone, addresses, notes) are user-editable and persist across
 * reconciles. `effectiveEmail` / `effectivePhone` already combine the
 * job-level override with the firm-level fallback so the table can render
 * a single display value.
 */
export interface JobContactRow {
  _id: Id<"jobContacts">;
  _creationTime: number;
  jobId: Id<"jobs">;
  orgId: Id<"organizations">;
  displayName: string;
  contactType: ContactType;
  abn?: string;
  acn?: string;
  email?: string;
  phone?: string;
  addresses: Array<ContactAddress>;
  sourceLinks: Array<ContactSourceLink>;
  roles?: ContactRole[];
  /** True when a practitioner manually overrode roles (MOR-676). */
  rolesOverridden?: boolean;
  /** Org-defined custom roles assigned to this contact (always manual). */
  customRoleIds?: Id<"jobContactRoles">[];
  firmContactId?: Id<"firmContacts">;
  firmContact?: FirmContactOverlay;
  effectiveEmail?: string;
  effectivePhone?: string;
  emailFromFirm: boolean;
  phoneFromFirm: boolean;
  mergedIntoId?: Id<"jobContacts">;
  mergeConfidence: MergeConfidence;
  status: ContactStatus;
  lastSeenAt: number;
  lastEditedBy?: Id<"users">;
  /** Corporate-group membership (MOR-708); absent = standalone contact. */
  groupId?: Id<"contactGroups">;
  /** Group display name resolved server-side for list rendering. */
  groupName?: string;
  /** True when this contact is its group's primary ("top co"). */
  isGroupPrimary?: boolean;
  /**
   * Company ↔ contact-person link. On a person contact, the company it is a
   * contact person for; on a company contact, the designated primary person.
   */
  linkedCompanyContactId?: Id<"jobContacts">;
  primaryContactPersonId?: Id<"jobContacts">;
  /** Display name of the linked company (person contacts). */
  linkedCompanyName?: string;
  /** Resolved primary contact person overlay (company contacts). */
  primaryContactPerson?: {
    _id: Id<"jobContacts">;
    displayName: string;
    firstName?: string;
    middleName?: string;
    lastName?: string;
    email?: string;
    phone?: string;
  };
  /**
   * External administration overlay (customer request). A non-empty status
   * means the company is in external administration; the appointee is
   * another job contact, resolved server-side with its firm.
   */
  externalAdminStatus?: string;
  externalAdminAppointeeContactId?: Id<"jobContacts">;
  externalAdminAppointeeTitle?: string;
  externalAdminAppointee?: {
    _id: Id<"jobContacts">;
    displayName: string;
    contactType: ContactType;
    firstName?: string;
    middleName?: string;
    lastName?: string;
    email?: string;
    phone?: string;
    addresses: Array<ContactAddress>;
    firm?: {
      _id: Id<"jobContacts">;
      displayName: string;
      email?: string;
      phone?: string;
      addresses: Array<ContactAddress>;
    };
  };
  /** Companies this contact (or its linked firm) is appointee of. */
  administeredCompanies?: Array<{
    _id: Id<"jobContacts">;
    displayName: string;
    externalAdminStatus?: string;
  }>;
}

/**
 * One sibling job contact flagged as a possible duplicate of the contact
 * currently being viewed in the detail panel. Computed server-side by
 * `api.contacts.queries.getJobContact`.
 */
export interface MergeSuggestion {
  contactId: Id<"jobContacts">;
  displayName: string;
  contactType: ContactType;
  abn?: string;
  acn?: string;
  email?: string;
  phone?: string;
  /** Distinct source-kind count on the suggested contact. */
  sourceKindCount: number;
  /** Reasons this contact looks like a duplicate. */
  matchedOn: Array<
    | "name"
    | "name_fuzzy"
    | "email"
    | "phone"
    | "acn"
    | "abn"
    | "bankrupt_role"
    | "date_of_birth"
  >;
}

/**
 * Suggest-only hint that a company contact is in external administration,
 * derived from the corporate register during ingestion and resolved by
 * `api.contacts.queries.getJobContact`. Nothing is written to the
 * authoritative `externalAdmin*` fields until the practitioner confirms.
 */
export interface ExternalAdminSuggestion {
  /** Suggested status label, e.g. "In Liquidation". */
  status?: string;
  appointees: Array<{
    /** Appointee name as the source spelled it. */
    name: string;
    /** Appointment title parsed from the source, e.g. "Liquidator". */
    title?: string;
    /** Matching active job contact, when one exists. */
    contactId?: Id<"jobContacts">;
    contactType?: ContactType;
  }>;
}

export interface JobContactWithHistory extends JobContactRow {
  changeHistory: Doc<"jobContacts">["changeHistory"];
  mergeSuggestions: Array<MergeSuggestion>;
  externalAdminSuggestion?: ExternalAdminSuggestion;
}

// ---------------------------------------------------------------------------
// Source-kind display metadata
//
// Lives in `src/lib/contact-roles.ts` so the precedent library can import
// it without pulling in this UI module. Re-exported here so existing
// `import { SOURCE_KIND_META } from "@/components/jobs/phases/contacts/types"`
// call-sites keep working.
// ---------------------------------------------------------------------------

export type SourceKindMeta = SharedSourceKindMeta;
export const SOURCE_KIND_META = SHARED_SOURCE_KIND_META;
export const SOURCE_KIND_ORDER = SHARED_SOURCE_KIND_ORDER;

export type RoleMeta = SharedRoleMeta;
export const ROLE_META = SHARED_ROLE_META;
export const ROLE_ORDER = SHARED_ROLE_ORDER;

// ---------------------------------------------------------------------------
// Custom (org-defined) job-contact roles
//
// User-defined roles that sit alongside the built-in `ContactRole` enum,
// stored on `jobContacts.customRoleIds` and defined per-org in
// `jobContactRoles`. Mirrors the firm-side `FirmContactCustomCategory`.
// ---------------------------------------------------------------------------

/** An org-defined custom job-contact role, as returned by
 * `api.contacts.customRoles.listJobContactRoles`. */
export interface CustomJobRole {
  _id: Id<"jobContactRoles">;
  name: string;
  color: TaskColor;
  icon?: WorkflowRoleIcon;
  description?: string;
  sortOrder: number;
}

/** Resolved display metadata for a custom role badge. */
export interface CustomRoleDisplay {
  /** Stable grouping/filter key. */
  key: string;
  id: Id<"jobContactRoles">;
  label: string;
  icon: LucideIcon;
  badgeColor: BadgeColor;
}

/** Grouping/filter key for a custom role. */
export function customRoleKey(id: Id<"jobContactRoles">): string {
  return `custom-role:${id}`;
}

/**
 * Resolve a contact's `customRoleIds` to display metadata, in the org's sort
 * order. Ids pointing at an archived / unknown role (absent from `customById`)
 * are silently dropped, so archived roles degrade gracefully.
 */
export function resolveCustomRoleDisplays(
  customRoleIds: ReadonlyArray<Id<"jobContactRoles">> | undefined,
  customById: ReadonlyMap<string, CustomJobRole>,
): Array<CustomRoleDisplay> {
  if (!customRoleIds || customRoleIds.length === 0) return [];
  const resolved: Array<CustomRoleDisplay> = [];
  for (const id of customRoleIds) {
    const role = customById.get(id);
    if (!role) continue;
    resolved.push({
      key: customRoleKey(role._id),
      id: role._id,
      label: role.name,
      icon: roleIconComponent(role.icon) ?? Tag,
      badgeColor: role.color,
    });
  }
  return resolved.sort((a, b) => {
    const ra = customById.get(a.id);
    const rb = customById.get(b.id);
    return (ra?.sortOrder ?? 0) - (rb?.sortOrder ?? 0);
  });
}

// ---------------------------------------------------------------------------
// Contact-type display metadata
// ---------------------------------------------------------------------------

export const CONTACT_TYPE_META: Record<
  ContactType,
  { label: string; icon: LucideIcon }
> = {
  person: { label: "Person", icon: UserSquare },
  organisation: { label: "Organisation", icon: Building2 },
  trust: { label: "Trust", icon: Briefcase },
  authority: { label: "Authority", icon: Landmark },
  unknown: { label: "Unknown", icon: Building2 },
};

export const CONTACT_TYPE_ORDER: ReadonlyArray<ContactType> = [
  "person",
  "organisation",
  "trust",
  "authority",
  "unknown",
];

// ---------------------------------------------------------------------------
// Firm-category display metadata
// ---------------------------------------------------------------------------

export const FIRM_CATEGORY_META: Record<
  FirmContactCategory,
  {
    label: string;
    shortLabel: string;
    icon: LucideIcon;
    badgeColor: SourceKindMeta["badgeColor"];
  }
> = {
  bank: {
    label: "Bank",
    shortLabel: "Bank",
    icon: Landmark,
    badgeColor: "blue",
  },
  regulator: {
    label: "Regulator",
    shortLabel: "Regulator",
    icon: ShieldCheck,
    badgeColor: "amber",
  },
  professional: {
    label: "Professional",
    shortLabel: "Professional",
    icon: Briefcase,
    badgeColor: "purple",
  },
  org_member: {
    label: "Org Member",
    shortLabel: "Org Member",
    icon: UserSquare,
    badgeColor: "emerald",
  },
  referrer: {
    label: "Referrer",
    shortLabel: "Referrer",
    icon: Handshake,
    badgeColor: "teal",
  },
  other: {
    label: "Other",
    shortLabel: "Other",
    icon: Building,
    badgeColor: "gray",
  },
};

export const FIRM_CATEGORY_ORDER: ReadonlyArray<FirmContactCategory> = [
  "bank",
  "regulator",
  "professional",
  "org_member",
  "referrer",
  "other",
];

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

/** Blank address row appended by "Add address" controls. */
export function createEmptyAddress(): ContactAddress {
  return {
    line1: "",
    line2: "",
    suburb: "",
    state: "",
    postcode: "",
    country: "Australia",
    sourceLabel: "Added manually",
  };
}

/** Format an AUD figure for display. Returns "—" when nullish. */
export function formatAud(amount: number | null | undefined): string {
  if (amount == null) return "—";
  return amount.toLocaleString("en-AU", {
    style: "currency",
    currency: "AUD",
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  });
}

/**
 * Format an ABN as "XX XXX XXX XXX" (11 digits).
 * Falls back to the raw string if the digit count is wrong.
 */
export function formatAbn(abn: string | undefined): string {
  if (!abn) return "";
  if (abn.length !== 11) return abn;
  return `${abn.slice(0, 2)} ${abn.slice(2, 5)} ${abn.slice(5, 8)} ${abn.slice(8, 11)}`;
}

/** Format an ACN as "XXX XXX XXX" (9 digits). */
export function formatAcn(acn: string | undefined): string {
  if (!acn) return "";
  if (acn.length !== 9) return acn;
  return `${acn.slice(0, 3)} ${acn.slice(3, 6)} ${acn.slice(6, 9)}`;
}

/** Aggregate distinct source kinds for a contact, in canonical order. */
export function distinctSourceKinds(
  contact: Pick<JobContactRow, "sourceLinks">,
): Array<ContactSourceKind> {
  const set = new Set<ContactSourceKind>();
  for (const s of contact.sourceLinks) set.add(s.kind);
  return SOURCE_KIND_ORDER.filter((k) => set.has(k));
}

/** Aggregate distinct roles for a contact, in canonical order. */
export function distinctRoles(
  contact: Pick<JobContactRow, "roles">,
): Array<ContactRole> {
  if (!contact.roles || contact.roles.length === 0) return [];
  const set = new Set<ContactRole>(contact.roles);
  return ROLE_ORDER.filter((r) => set.has(r));
}

/** Exposure totals split by direction. */
export interface ExposureSplit {
  /** Amount the company owes this contact (liability). */
  creditor: number;
  /** Amount this contact owes the company (receivable). */
  debtor: number;
  /** Amount with no direction tag (asset valuations, legacy data). */
  unclassified: number;
}

/** Split exposure by creditor / debtor direction. */
export function splitExposure(
  contact: Pick<JobContactRow, "sourceLinks">,
): ExposureSplit {
  return contact.sourceLinks.reduce<ExposureSplit>(
    (totals, s) => {
      if (typeof s.amount !== "number" || s.amount === 0) return totals;
      if (s.exposureDirection === "creditor") {
        return { ...totals, creditor: totals.creditor + s.amount };
      }
      if (s.exposureDirection === "debtor") {
        return { ...totals, debtor: totals.debtor + s.amount };
      }
      return { ...totals, unclassified: totals.unclassified + s.amount };
    },
    { creditor: 0, debtor: 0, unclassified: 0 },
  );
}

/** Sum of `amount`s across all source links (treating undefined as 0). */
export function totalExposure(
  contact: Pick<JobContactRow, "sourceLinks">,
): number {
  const { creditor, debtor, unclassified } = splitExposure(contact);
  return creditor + debtor + unclassified;
}
